import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams, Navigate } from "react-router-dom";
import { useAuth, AppRouteEffects } from "@/shared/hooks";
import { Routes, Route } from "react-router-dom";
import { ChessGameDashboard as TournamentPage } from "@/features/games/chess";
import Home from "./features/home/Home";
import { PoolGameDashboard } from "@/features/games/pool";
import { AppDashboard } from "@/features/home";
import { Levels } from "@/features/levels";
import { Villager } from "@/features/villager";
import { Background, GlobalNotifications, Header } from "./components";
import ConnectionStatusBanner from "./components/ConnectionStatusBanner";
import "@/css/App.css";
import AuthContainer from "./components/AuthContainer";
import { AUTH, type AuthState } from "@/shared/constants/auth";
import { GAME } from "./shared/constants/game";

function LegacyPoolRouteRedirect() {
  const { legacyID } = useParams<{ legacyID?: string }>();
  const value = (legacyID || "").trim();
  if (!value) {
    return <Navigate to="/pool" replace />;
  }

  // Back-compat:
  // - numeric ids were legacy session ids
  // - non-numeric ids are treated as room ids
  const isSessionID = /^\d+$/.test(value);
  return <Navigate to={isSessionID ? `/pool/sessions/${value}` : `/pool/rooms/${value}`} replace />;
}

function App() {
  const { appUser, loading, refetchAppUser } = useAuth();
  const [ authState, setAuthState ] = useState<AuthState>(AUTH.NONE);
  const navigate = useNavigate();
  const location = useLocation();
  
  const getGameClass = () => {
    if (location.pathname.startsWith('/chess')) return 'chess';
    if (location.pathname.startsWith('/pool')) return 'pool';
    if (location.pathname.startsWith('/levels')) return 'levels';
    if (location.pathname.startsWith('/villager')) return 'villager';
    return 'none-bck';
  };

  useEffect(() => {
    if (authState === AUTH.LOGIN && !appUser) {
      navigate("/login");
    } else if (authState === AUTH.REGISTER && !appUser) {
      navigate("/register");
    }
  }, [authState, appUser, navigate]);

  function changeAuthState(){
      setAuthState(authState === AUTH.LOGIN ? AUTH.REGISTER : AUTH.LOGIN)
  }

  if (loading) return <div>Loading...</div>;

  return (
    <>
      <AppRouteEffects />
      <ConnectionStatusBanner />
      <GlobalNotifications />
      <div className={`App ${getGameClass()}`}>
        <Header user={appUser} refetchUser={refetchAppUser} setAuthState={setAuthState} game={getGameClass()}/>
        <Background game={getGameClass()}/>
        <Routes>
          <Route
              path="/"
              element={<AppDashboard user={appUser}  setAuthState={setAuthState}/>}
            />
            <Route
              path="/login"
              element={
                <AuthContainer
                  authState={AUTH.LOGIN}
                  changeAuthState={changeAuthState}
                  setAuthState={setAuthState}
                  onComplete={async () => {
                    await refetchAppUser();
                    navigate("/");
                    setAuthState(AUTH.NONE);
                  }}
                />
              }
            />
            <Route
              path="/register"
              element={
                <AuthContainer
                  authState={AUTH.REGISTER}
                  changeAuthState={changeAuthState}
                  setAuthState={setAuthState}
                  onComplete={() => {
                    setAuthState(AUTH.NONE);
                    navigate("/");
                    refetchAppUser();
                  }}
                />
              }
            />
            <Route
              path="/auth/discord/callback"
              element={
                <AuthContainer
                  authState={AUTH.REGISTER}
                  changeAuthState={changeAuthState}
                  setAuthState={setAuthState}
                  onComplete={() => {
                    setAuthState(AUTH.NONE);
                    navigate("/");
                    refetchAppUser();
                  }}
                />
              }
            />
            <Route
              path="/chess"
              element={
                <Home 
                  user={appUser} 
                  refetchUser={refetchAppUser} 
                  gameState={GAME.CHESS}
                />
              }
            />
            <Route
              path="/pool"
              element={
                <Home
                  user={appUser}
                  refetchUser={refetchAppUser}
                  gameState={GAME.POOL}
                />
              }
            />
            <Route
              path="/levels"
              element={<Levels user={appUser} />}
            />
            <Route
              path="/villager"
              element={<Villager user={appUser} />}
            />
            <Route
              path="/pool/sessions/:sessionID"
              element={<PoolGameDashboard user={appUser} gameState={GAME.POOL} />}
            />
            <Route
              path="/pool/rooms/:roomID"
              element={<PoolGameDashboard user={appUser} gameState={GAME.POOL} />}
            />
            <Route path="/pool/:legacyID" element={<LegacyPoolRouteRedirect />} />
            <Route
              path="/chess/:sessionID"
              element={<TournamentPage user={appUser} gameState={GAME.CHESS} />}
            />
            <Route
              path="/chess/:sessionID/:gameID"
              element={<TournamentPage user={appUser} gameState={GAME.CHESS} />}
            />
          </Routes>
      </div>
    </>
  );
}


export default App;
